import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormikErrors, FormikHandlers, useFormik } from 'formik'
import * as Yup from 'yup'
import InputGroup from './elements/InputGroup'
import SelectDropdown from './elements/SelectDropdown'
import FreightClassTooltip from './elements/FreightClassTooltip'
import { ExitIcon } from '../icons/ExitIcon'
import { InputCheckbox } from './elements/Inputs'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { useSession } from 'next-auth/react'
import type { AccountNamesAndIds } from '@/libs/types'
import Spinner from '../shared/Spinner'
import Toast from '../shared/Toast'
import { event } from '@/utils/gtag'
import { getQuoteNumber, getQuoteQueryKey } from '@/utils/quotes'
import {
  GoogleAutocompleteSuggestion,
  GooglePlacePrediction,
  parseGoogleAddress,
} from '@/utils/googlePlacesAddress'

export interface ShipmentItem {
  weight: string
  freightClass: string
  length: number | null
  width: number | null
  height: number | null
  handlingUnits: number | null
  hazardous: boolean
  description: string
}

const HAZM_CODE = 'HAZM'
const LIFTGATE_PICKUP_CODE = 'LIPU'
const LIFTGATE_DELIVERY_CODE = 'LIFD'
const VOLUME_PRICING_LIMITS = {
  weight: 10000,
  width: 98,
  height: 108,
}
// The backend auto-rates Linear Foot Rule charges (LF20/LF40/LF60) on total
// occupied length up to 45 linear feet, and Overlength charges per item
// length; beyond 45 linear feet still requires volume pricing.
const MAX_LINEAR_FEET = 45
const LIFTGATE_HANDLING_UNIT_LIMITS = {
  length: 72,
  width: 48,
  height: 96,
  weight: 2500,
}

interface ServicePoint {
  state: string
  city: string
  zipCode: number
  serviceType: string
  nptAddOnDays: number
  nptTerminal: number
  extendedDeliveryCharge: number
  agentFee: number
}

interface ZipValidationResponse {
  statusCode: number
  originServicePoint?: ServicePoint
  destinationServicePoint?: ServicePoint
}

export interface RateEstimateFormValues {
  originAddressLine1: string
  originAddressLine2: string
  originAddressNotFound: boolean
  originZip: string
  originCity: string
  originState: string
  originCountry: string
  destinationAddressLine1: string
  destinationAddressLine2: string
  destinationAddressNotFound: boolean
  destinationZip: string
  destinationCity: string
  destinationState: string
  destinationCountry: string
  shipmentItems: ShipmentItem[]
  accessorials: string[]
}

interface RateEstimateFormProps {
  initialValues?: Partial<RateEstimateFormValues>
  googlePlacesReady?: boolean
}

type AddressSearchTarget = 'origin' | 'destination'

export const ACCESSORIALS: { code: string; label: string }[] = [
  { code: 'AIRP', label: 'Airport Delivery' },
  { code: 'LIFD', label: 'Lift Gate Delivery' },
  { code: 'NOTI', label: 'Notification Charge' },
  { code: 'RESI', label: 'Residential Delivery' },
  { code: 'BILL', label: 'Bill Of Lading' },
  { code: 'LIPU', label: 'Lift Gate Pickup' },
  { code: 'POD1', label: 'POD Required w/ Invoice' },
  { code: 'RWGH', label: 'Reweigh' },
  { code: 'HAZM', label: 'Hazardous Material Fee' },
  { code: 'LIMI', label: 'Limited Access Delivery' },
  { code: 'FREE', label: 'Protect From Freezing' },
  { code: 'APPT', label: 'Scheduled Appointment Delivery' },
  { code: 'INSI', label: 'Inside Service Delivery' },
  { code: 'LIMP', label: 'Limited Access Pickup' },
  { code: 'REDE', label: 'Redelivery' },
  { code: 'STGD', label: 'Storage Destination' },
  { code: 'INPU', label: 'Inside Service Pickup' },
  { code: 'MARK', label: 'Marking And Tagging' },
  { code: 'REPU', label: 'Residential Pickup' },
  { code: 'STGO', label: 'Storage Origin' },
]

const FREIGHT_CLASSES = [
  { text: '50', value: '50' },
  { text: '55', value: '55' },
  { text: '60', value: '60' },
  { text: '65', value: '65' },
  { text: '70', value: '70' },
  { text: '77.5', value: '77.5' },
  { text: '85', value: '85' },
  { text: '92.5', value: '92.5' },
  { text: '100', value: '100' },
  { text: '125', value: '125' },
  { text: '150', value: '150' },
  { text: '175', value: '175' },
  { text: '250', value: '250' },
  { text: '300', value: '300' },
]

const requiredPositiveNumber = Yup.number()
  .nullable()
  .required('Required')
  .moreThan(0, 'Must be greater than 0')

const requiredHandlingUnits = Yup.number()
  .nullable()
  .required('Required')
  .min(1, 'Must be greater than or equal to 1')

const validationSchema = Yup.object({
  originAddressLine1: Yup.string().when(
    'originAddressNotFound',
    (originAddressNotFound, schema) =>
      originAddressNotFound[0] ? schema : schema.required('Required')
  ),
  originZip: Yup.string().required('Required'),
  destinationAddressLine1: Yup.string().when(
    'destinationAddressNotFound',
    (destinationAddressNotFound, schema) =>
      destinationAddressNotFound[0] ? schema : schema.required('Required')
  ),
  destinationZip: Yup.string().required('Required'),
  shipmentItems: Yup.array().of(
    Yup.object({
      weight: requiredPositiveNumber,
      freightClass: Yup.string().required('Required'),
      length: requiredPositiveNumber,
      width: requiredPositiveNumber,
      height: requiredPositiveNumber,
      handlingUnits: requiredHandlingUnits,
    })
  ),
})

const getRateEstimateErrorMessage = async (res: Response) => {
  try {
    const data = await res.json()

    if (
      data?.error === 'No active agreement' &&
      typeof data.body === 'string'
    ) {
      return data.body
    }
  } catch {
    return 'Rate estimate request failed. Please try again.'
  }

  return 'Rate estimate request failed. Please try again.'
}

const shipmentRequiredFields: Array<{
  field: keyof Omit<ShipmentItem, 'hazardous'>
  label: string
}> = [
  { field: 'weight', label: 'Total Weight' },
  { field: 'freightClass', label: 'Freight Class' },
  { field: 'length', label: 'Length' },
  { field: 'width', label: 'Width' },
  { field: 'height', label: 'Height' },
  { field: 'handlingUnits', label: 'Handling Units' },
]

const getMissingRequiredFieldMessage = (
  errors: FormikErrors<RateEstimateFormValues>,
  includeShipmentFields = true
) => {
  if (errors.originAddressLine1) return 'Please complete Origin Address.'
  if (errors.originZip) return 'Please complete Origin Zip.'
  if (errors.destinationAddressLine1) {
    return 'Please complete Destination Address.'
  }
  if (errors.destinationZip) return 'Please complete Destination Zip.'

  if (includeShipmentFields && Array.isArray(errors.shipmentItems)) {
    for (let index = 0; index < errors.shipmentItems.length; index += 1) {
      const itemErrors = errors.shipmentItems[index]
      if (!itemErrors || typeof itemErrors === 'string') continue

      for (let i = 0; i < shipmentRequiredFields.length; i += 1) {
        const { field, label } = shipmentRequiredFields[i]
        if (itemErrors[field]) {
          return `Please complete Shipment Item ${index + 1} ${label}.`
        }
      }
    }
  }

  return null
}

const RateEstimateForm: React.FunctionComponent<RateEstimateFormProps> = ({
  initialValues,
  googlePlacesReady = false,
}) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { status } = useSession()
  const [isValidatingZips, setIsValidatingZips] = useState(false)
  const [zipValidationError, setZipValidationError] = useState<string | null>(
    null
  )
  const [destinationInterlineNotice, setDestinationInterlineNotice] =
    useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [zipsValidated, setZipsValidated] = useState<boolean>(false)
  const [zipValidationRequestToken, setZipValidationRequestToken] = useState(0)
  const [originPredictions, setOriginPredictions] = useState<
    GooglePlacePrediction[]
  >([])
  const [destinationPredictions, setDestinationPredictions] = useState<
    GooglePlacePrediction[]
  >([])
  const [originActiveIndex, setOriginActiveIndex] = useState(-1)
  const [destinationActiveIndex, setDestinationActiveIndex] = useState(-1)
  const [originManualAddress, setOriginManualAddress] = useState(false)
  const [destinationManualAddress, setDestinationManualAddress] =
    useState(false)
  const [data3PL, setData3PL] = useState<{
    is3plUser: boolean
    accountNamesAndIds: AccountNamesAndIds[]
  } | null>(null)
  const [selected3PLManagedAccount, setSelected3PLManagedAccount] = useState('')
  const originAddressRef = useRef<HTMLInputElement>(null)
  const destinationAddressRef = useRef<HTMLInputElement>(null)
  const autocompleteSuggestionRef = useRef<GoogleAutocompleteSuggestion | null>(
    null
  )
  const createSessionTokenRef = useRef<(() => unknown) | null>(null)
  const sessionTokenRef = useRef<unknown | null>(null)
  const predictionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const predictionRequestIdRef = useRef(0)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastKey, setToastKey] = useState(0)
  const handleToastClose = useCallback(() => setToastVisible(false), [])

  const startedRef = useRef(false)
  const handleFirstInteraction = () => {
    if (startedRef.current) return
    startedRef.current = true
    event({ action: 'rate_estimate_start', category: 'quote' })
  }

  useEffect(() => {
    if (originAddressRef.current) {
      originAddressRef.current.value = initialValues?.originAddressLine1 ?? ''
    }
    if (destinationAddressRef.current) {
      destinationAddressRef.current.value =
        initialValues?.destinationAddressLine1 ?? ''
    }
  }, [initialValues])

  const formik = useFormik<RateEstimateFormValues>({
    validationSchema,
    enableReinitialize: true,
    initialValues: {
      originAddressLine1: '',
      originAddressLine2: '',
      originAddressNotFound: false,
      originZip: '',
      originCity: '',
      originState: '',
      originCountry: 'US',
      destinationAddressLine1: '',
      destinationAddressLine2: '',
      destinationAddressNotFound: false,
      destinationZip: '',
      destinationCity: '',
      destinationState: '',
      destinationCountry: 'US',
      shipmentItems: [
        {
          weight: '',
          freightClass: '',
          length: null,
          width: null,
          height: null,
          handlingUnits: null,
          hazardous: false,
          description: '',
        },
      ],
      accessorials: [],
      ...initialValues,
    },
    onSubmit: async (values) => {
      const payload = {
        ...values,
        totalCubicFeet: computedTotalCubicFeet.toFixed(2),
      }
      await handleSubmit(payload)
    },
  })

  const is3plUser = Boolean(data3PL?.is3plUser)
  const managedCustomers = data3PL?.accountNamesAndIds ?? []
  const showCustomerDropdown = is3plUser && managedCustomers.length > 0
  const customerOptions = managedCustomers.map((customer) => ({
    text: customer.name,
    value: customer.nopk600BillToCode ?? '',
    disabled: !customer.nopk600BillToCode,
  }))
  const handleCustomerChange = ((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelected3PLManagedAccount(e.target.value)
  }) as FormikHandlers['handleChange']
  const handleCustomerBlur = (() => undefined) as FormikHandlers['handleBlur']
  const handleNonNegativeNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.value.includes('-')) {
      e.target.value = e.target.value.replace(/-/g, '')
    }
    formik.handleChange(e)
  }

  useEffect(() => {
    if (status !== 'authenticated') {
      setData3PL(null)
      setSelected3PLManagedAccount('')
      return
    }
    let cancelled = false
    fetch('/api/internal-api/quote-customers')
      .then(async (res) => {
        if (cancelled || !res.ok) return
        const data = await res.json()
        if (cancelled) return
        setData3PL({
          is3plUser: Boolean(data?.is3plUser),
          accountNamesAndIds: Array.isArray(data?.accountNamesAndIds)
            ? data.accountNamesAndIds
            : [],
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [status])

  useEffect(() => {
    if (!router.isReady) return
    const customers = data3PL?.accountNamesAndIds ?? []
    if (!data3PL?.is3plUser || customers.length === 0) return
    setSelected3PLManagedAccount((current) => {
      if (current) return current
      const urlAccountNumber = Array.isArray(router.query.accountNumber)
        ? router.query.accountNumber[0]
        : router.query.accountNumber
      const matchedCode = customers.find(
        (customer) =>
          customer.nopk600BillToCode &&
          customer.nopk600BillToCode === urlAccountNumber
      )?.nopk600BillToCode
      return (
        matchedCode ??
        customers.find((customer) => customer.nopk600BillToCode)
          ?.nopk600BillToCode ??
        ''
      )
    })
  }, [router.isReady, data3PL, router.query.accountNumber])

  const originAddressHasError = Boolean(
    (formik.submitCount > 0 || formik.touched.originAddressLine1) &&
      formik.errors.originAddressLine1
  )
  const destinationAddressHasError = Boolean(
    (formik.submitCount > 0 || formik.touched.destinationAddressLine1) &&
      formik.errors.destinationAddressLine1
  )

  useEffect(() => {
    if (!googlePlacesReady || !window.google?.maps?.importLibrary) return

    let cancelled = false

    window.google.maps
      .importLibrary('places')
      .then(({ AutocompleteSuggestion, AutocompleteSessionToken }) => {
        if (cancelled) return
        autocompleteSuggestionRef.current = AutocompleteSuggestion
        createSessionTokenRef.current = () => new AutocompleteSessionToken()
        sessionTokenRef.current = createSessionTokenRef.current()
      })
      .catch(() => {
        autocompleteSuggestionRef.current = null
      })

    return () => {
      cancelled = true
      if (predictionTimeoutRef.current) {
        clearTimeout(predictionTimeoutRef.current)
      }
      autocompleteSuggestionRef.current = null
      createSessionTokenRef.current = null
      sessionTokenRef.current = null
    }
  }, [googlePlacesReady])

  const setPredictions = (
    target: AddressSearchTarget,
    predictions: GooglePlacePrediction[]
  ) => {
    if (target === 'origin') {
      setOriginPredictions(predictions)
      setOriginActiveIndex(-1)
      return
    }

    setDestinationPredictions(predictions)
    setDestinationActiveIndex(-1)
  }

  const handleAddressKeyDown = (
    target: AddressSearchTarget,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const predictions =
      target === 'origin' ? originPredictions : destinationPredictions
    const activeIndex =
      target === 'origin' ? originActiveIndex : destinationActiveIndex
    const setActiveIndex =
      target === 'origin' ? setOriginActiveIndex : setDestinationActiveIndex

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (predictions.length === 0) return
      setActiveIndex((i) => (i < predictions.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (predictions.length === 0) return
      setActiveIndex((i) => (i > 0 ? i - 1 : predictions.length - 1))
    } else if (
      e.key === 'Enter' &&
      activeIndex >= 0 &&
      predictions[activeIndex]
    ) {
      e.preventDefault()
      handleAddressPredictionSelect(target, predictions[activeIndex])
      setActiveIndex(-1)
    } else if (e.key === 'Escape') {
      setPredictions(target, [])
    }
  }

  const handleAddressSearchChange = (
    target: AddressSearchTarget,
    value: string
  ) => {
    const isManualAddress =
      target === 'origin' ? originManualAddress : destinationManualAddress

    if (predictionTimeoutRef.current) {
      clearTimeout(predictionTimeoutRef.current)
    }

    setZipsValidated(false)
    setZipValidationError(null)

    if (isManualAddress) {
      formik.setFieldValue(`${target}AddressLine1`, value)
      setPredictions(target, [])
      return
    }

    formik.setFieldValue(`${target}AddressLine1`, '')
    formik.setFieldValue(`${target}City`, '')
    formik.setFieldValue(`${target}State`, '')
    formik.setFieldValue(`${target}Zip`, '')
    formik.setFieldValue(`${target}Country`, 'US')

    const input = value.trim()
    if (input.length < 3 || !autocompleteSuggestionRef.current) {
      setPredictions(target, [])
      return
    }

    const requestId = ++predictionRequestIdRef.current
    predictionTimeoutRef.current = setTimeout(() => {
      autocompleteSuggestionRef.current
        ?.fetchAutocompleteSuggestions({
          input,
          includedRegionCodes: ['us'],
          sessionToken: sessionTokenRef.current,
        })
        .then(({ suggestions }) => {
          if (requestId !== predictionRequestIdRef.current) {
            return
          }

          const predictions = suggestions
            .map((suggestion) => suggestion.placePrediction)
            .filter(Boolean) as GooglePlacePrediction[]

          if (predictions.length === 0) {
            if (target === 'origin') {
              setOriginManualAddress(true)
            } else {
              setDestinationManualAddress(true)
            }
            formik.setFieldValue(`${target}AddressLine1`, input)
          }

          setPredictions(target, predictions)
        })
        .catch(() => {
          setPredictions(target, [])
        })
    }, 250)
  }

  const handleManualAddressToggle = (
    target: AddressSearchTarget,
    checked: boolean
  ) => {
    if (target === 'origin') {
      setOriginManualAddress(checked)
      formik.setFieldValue('originAddressNotFound', checked)
      if (checked) {
        formik.setFieldValue(
          'originAddressLine1',
          originAddressRef.current?.value ?? ''
        )
        setOriginPredictions([])
      }
      return
    }

    setDestinationManualAddress(checked)
    formik.setFieldValue('destinationAddressNotFound', checked)
    if (checked) {
      formik.setFieldValue(
        'destinationAddressLine1',
        destinationAddressRef.current?.value ?? ''
      )
      setDestinationPredictions([])
    }
  }

  const appliedUrlZipParamsRef = useRef(false)
  useEffect(() => {
    if (!router.isReady || appliedUrlZipParamsRef.current) return
    appliedUrlZipParamsRef.current = true

    const urlOriginZip = Array.isArray(router.query.originZip)
      ? router.query.originZip[0]
      : router.query.originZip
    const urlDestinationZip = Array.isArray(router.query.destZip)
      ? router.query.destZip[0]
      : router.query.destZip

    if (urlOriginZip) {
      formik.setFieldValue('originZip', urlOriginZip)
      handleManualAddressToggle('origin', true)
    }
    if (urlDestinationZip) {
      formik.setFieldValue('destinationZip', urlDestinationZip)
      handleManualAddressToggle('destination', true)
    }
  }, [router.isReady, router.query.originZip, router.query.destZip])

  const handleAddressPredictionSelect = async (
    target: AddressSearchTarget,
    prediction: GooglePlacePrediction
  ) => {
    const description = prediction.text.toString()

    const input =
      target === 'origin'
        ? originAddressRef.current
        : destinationAddressRef.current
    setPredictions(target, [])

    try {
      const place = prediction.toPlace()
      await place.fetchFields?.({
        fields: ['addressComponents'],
      })

      const parsedAddress = parseGoogleAddress(place)

      if (input) input.value = parsedAddress.street || description

      if (target === 'origin') {
        setOriginManualAddress(false)
      } else {
        setDestinationManualAddress(false)
      }

      if (parsedAddress.street) {
        formik.setFieldValue(`${target}AddressLine1`, parsedAddress.street)
      }
      if (parsedAddress.zip) {
        formik.setFieldValue(`${target}Zip`, parsedAddress.zip)
      }
      if (parsedAddress.city) {
        formik.setFieldValue(`${target}City`, parsedAddress.city)
      }
      if (parsedAddress.state) {
        formik.setFieldValue(`${target}State`, parsedAddress.state)
      }
      formik.setFieldValue(`${target}Country`, parsedAddress.country || 'US')

      setZipValidationRequestToken((token) => token + 1)
      sessionTokenRef.current = createSessionTokenRef.current?.() ?? null
    } catch {
      if (input) input.value = description
    }
  }

  const handleSubmit = async (
    payload: RateEstimateFormValues & { totalCubicFeet: string }
  ) => {
    if (isValidatingZips) {
      setSubmitError('Please wait while we validate the ZIP codes.')
      return
    }
    if (zipValidationError) {
      setSubmitError(zipValidationError)
      return
    }
    if (!zipsValidated) {
      setSubmitError('Please enter valid origin and destination ZIP codes.')
      return
    }
    if (volumePricingError) {
      setSubmitError(volumePricingError)
      return
    }
    if (liftgateLimitError) {
      setSubmitError(liftgateLimitError)
      return
    }
    if (is3plUser && !selected3PLManagedAccount) {
      setSubmitError('Please select a customer to quote for.')
      return
    }

    try {
      setSubmitError(null)

      const res = await fetch('/api/internal-api/rate-estimate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          accountNumber: is3plUser
            ? selected3PLManagedAccount
            : Array.isArray(router.query.accountNumber)
            ? router.query.accountNumber[0]
            : router.query.accountNumber,
        }),
      })

      if (!res.ok) {
        setSubmitError(await getRateEstimateErrorMessage(res))
        return
      }

      const data = await res.json()
      const quote = data?.quote
      const quoteRef = getQuoteNumber(quote)

      if (!quoteRef) {
        setSubmitError('Rate estimate request failed. Please try again.')
        return
      }

      // Seed the results page's query cache with the quote we just created
      // so it can render immediately instead of re-fetching what we already have.
      queryClient.setQueryData(getQuoteQueryKey(quoteRef), data)
      if (quote?.quoteId && quote.quoteId !== quoteRef) {
        queryClient.setQueryData(getQuoteQueryKey(quote.quoteId), data)
      }

      event({
        action: 'rate_estimate_submit',
        category: 'quote',
        label: `${payload.originZip}-${payload.destinationZip}`,
      })

      await router.push(
        `/rate-estimate/${encodeURIComponent(quoteRef)}/results`
      )
    } catch {
      setSubmitError('Rate estimate request failed. Please try again.')
    }
  }

  const validateZipCodes = async (
    originZip: string,
    destinationZip: string
  ): Promise<ZipValidationResponse | null> => {
    try {
      const response = await fetch('/api/internal-api/validate-zipcodes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originZipCode: Number(originZip),
          destinationZipCode: Number(destinationZip),
        }),
      })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    }
  }

  const addShipmentItem = () => {
    formik.setFieldValue('shipmentItems', [
      ...formik.values.shipmentItems,
      {
        weight: '',
        freightClass: '',
        length: null,
        width: null,
        height: null,
        handlingUnits: null,
        hazardous: false,
      },
    ])
  }

  const removeShipmentItem = (index: number) => {
    const nextItems = formik.values.shipmentItems.filter((_, i) => i !== index)
    formik.setFieldValue('shipmentItems', nextItems)
    syncHazmAccessorial(nextItems)
  }

  const syncHazmAccessorial = (items: ShipmentItem[]) => {
    const anyHazardous = items.some((item) => item.hazardous)
    const hasHazm = formik.values.accessorials.includes(HAZM_CODE)

    if (anyHazardous && !hasHazm) {
      formik.setFieldValue('accessorials', [
        ...formik.values.accessorials,
        HAZM_CODE,
      ])
    } else if (!anyHazardous && hasHazm) {
      formik.setFieldValue(
        'accessorials',
        formik.values.accessorials.filter((c) => c !== HAZM_CODE)
      )
    }
  }

  const toggleItemHazardous = (index: number) => {
    const nextItems = formik.values.shipmentItems.map((item, i) =>
      i === index ? { ...item, hazardous: !item.hazardous } : item
    )
    formik.setFieldValue('shipmentItems', nextItems)
    syncHazmAccessorial(nextItems)
  }

  const computedTotalCubicFeet = formik.values.shipmentItems.reduce(
    (total, item) => {
      const l = Number(item.length) || 0
      const w = Number(item.width) || 0
      const h = Number(item.height) || 0
      const units = Number(item.handlingUnits) || 1
      return total + ((l * w * h) / 1728) * units
    },
    0
  )

  const computedTotalWeight = formik.values.shipmentItems.reduce(
    (total, item) => {
      const weight = parseFloat(item.weight) || 0
      return total + weight
    },
    0
  )

  const computedTotalHandlingUnits = formik.values.shipmentItems.reduce(
    (total, item) => total + (Number(item.handlingUnits) || 0),
    0
  )

  // Mirrors the backend's Linear Foot Rule occupied-length calculation:
  // sum of each line's length times its handling units.
  const computedTotalLinearFeet = formik.values.shipmentItems.reduce(
    (total, item) => {
      const l = Number(item.length) || 0
      const units = Number(item.handlingUnits) || 1
      return total + (l * units) / 12
    },
    0
  )

  const computedPoundsPerCubicFoot =
    computedTotalCubicFeet > 0
      ? computedTotalWeight / computedTotalCubicFeet
      : 0

  const exceedsVolumePricingThreshold =
    computedTotalWeight > VOLUME_PRICING_LIMITS.weight ||
    computedTotalLinearFeet > MAX_LINEAR_FEET ||
    formik.values.shipmentItems.some((item) => {
      const w = Number(item.width) || 0
      const h = Number(item.height) || 0
      return w > VOLUME_PRICING_LIMITS.width || h > VOLUME_PRICING_LIMITS.height
    })

  const volumePricingError = exceedsVolumePricingThreshold
    ? `Shipments over ${MAX_LINEAR_FEET} linear feet, width over ${
        VOLUME_PRICING_LIMITS.width
      } inches, height over ${
        VOLUME_PRICING_LIMITS.height
      } inches, or weight over ${VOLUME_PRICING_LIMITS.weight.toLocaleString()} lbs require volume pricing. Please contact our Corporate Rate department at 303.295.0300`
    : null

  const liftgateAccessorialSelected =
    formik.values.accessorials.includes(LIFTGATE_PICKUP_CODE) ||
    formik.values.accessorials.includes(LIFTGATE_DELIVERY_CODE)

  const exceedsLiftgateHandlingUnitLimit =
    liftgateAccessorialSelected &&
    formik.values.shipmentItems.some((item) => {
      const l = Number(item.length) || 0
      const w = Number(item.width) || 0
      const h = Number(item.height) || 0
      const weight = parseFloat(item.weight) || 0
      return (
        l > LIFTGATE_HANDLING_UNIT_LIMITS.length ||
        w > LIFTGATE_HANDLING_UNIT_LIMITS.width ||
        h > LIFTGATE_HANDLING_UNIT_LIMITS.height ||
        weight > LIFTGATE_HANDLING_UNIT_LIMITS.weight
      )
    })

  const liftgateLimitError = exceedsLiftgateHandlingUnitLimit
    ? `Liftgate Pickup and Liftgate Delivery are limited to handling units under ${
        LIFTGATE_HANDLING_UNIT_LIMITS.length
      }x${LIFTGATE_HANDLING_UNIT_LIMITS.width}x${
        LIFTGATE_HANDLING_UNIT_LIMITS.height
      } inches and no more than ${LIFTGATE_HANDLING_UNIT_LIMITS.weight.toLocaleString()} lbs each. Please adjust your shipment details or remove the liftgate service.`
    : null

  const validationSubmitError =
    formik.submitCount > 0
      ? getMissingRequiredFieldMessage(formik.errors, zipsValidated)
      : null
  const zipSubmitError =
    formik.submitCount > 0 && !validationSubmitError && !zipsValidated
      ? zipValidationError ??
        'Please enter valid origin and destination ZIP codes.'
      : null
  const toastError = validationSubmitError
  const bannerError =
    zipSubmitError ??
    (formik.submitCount > 0 ? volumePricingError : null) ??
    (formik.submitCount > 0 ? liftgateLimitError : null) ??
    submitError

  useEffect(() => {
    if (!toastError) return

    setToastMessage(toastError)
    setToastKey((k) => k + 1)
    setToastVisible(true)

    requestAnimationFrame(() => {
      const firstInvalid = document.querySelector(
        '[aria-invalid="true"]'
      ) as HTMLElement
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [toastError, formik.submitCount])

  useEffect(() => {
    if (!bannerError) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [bannerError])

  useEffect(() => {
    const { originZip, destinationZip } = formik.values

    if (originZip.length < 5) {
      setZipsValidated(false)
      formik.setFieldValue('originCity', '')
      formik.setFieldValue('originState', '')
    }
    if (destinationZip.length < 5) {
      setZipsValidated(false)
      setDestinationInterlineNotice(false)
      formik.setFieldValue('destinationCity', '')
      formik.setFieldValue('destinationState', '')
    }

    if (originZip.length !== 5 || destinationZip.length !== 5) return

    setIsValidatingZips(true)
    setZipValidationError(null)

    validateZipCodes(originZip, destinationZip).then((data) => {
      setIsValidatingZips(false)
      const destinationServiceType = data?.destinationServicePoint?.serviceType
      setDestinationInterlineNotice(
        destinationServiceType === 'N' || destinationServiceType === 'I'
      )
      if (!data?.originServicePoint || !data?.destinationServicePoint) {
        const originInvalid = !data?.originServicePoint
        const destinationInvalid = !data?.destinationServicePoint

        event({
          action: 'rate_estimate_not_serviceable',
          category: 'quote',
          label: `${originZip}-${destinationZip}`,
        })

        if (originInvalid && destinationInvalid) {
          setZipValidationError(
            'Origin and destination ZIP codes are invalid or not serviceable.'
          )
        } else if (originInvalid) {
          setZipValidationError(
            'Origin ZIP code is invalid or not serviceable.'
          )
        } else {
          setZipValidationError(
            'Destination ZIP code is invalid or not serviceable.'
          )
        }

        if (originInvalid) {
          formik.setFieldValue('originCity', '')
          formik.setFieldValue('originState', '')
        }
        if (destinationInvalid) {
          formik.setFieldValue('destinationCity', '')
          formik.setFieldValue('destinationState', '')
        }
        return
      }

      const originIsDirect = data.originServicePoint.serviceType === 'D'
      const destinationIsDirect =
        data.destinationServicePoint.serviceType === 'D'

      formik.setFieldValue('originCity', data.originServicePoint.city)
      formik.setFieldValue('originState', data.originServicePoint.state)
      formik.setFieldValue('destinationCity', data.destinationServicePoint.city)
      formik.setFieldValue(
        'destinationState',
        data.destinationServicePoint.state
      )

      if (!originIsDirect && !destinationIsDirect) {
        event({
          action: 'rate_estimate_not_serviceable',
          category: 'quote',
          label: `interline:${originZip}-${destinationZip}`,
        })
        const toTitleCase = (s: string) =>
          s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
        const originCity = toTitleCase(data.originServicePoint.city)
        const destinationCity = toTitleCase(data.destinationServicePoint.city)
        setZipValidationError(
          `${originCity} and ${destinationCity} are both outside our direct service area, but we can still accommodate your shipment. Please contact sales at sales@nopk.biz.`
        )
        return
      }

      setZipValidationError(null)
      setZipsValidated(true)
    })
  }, [
    formik.values.originZip,
    formik.values.destinationZip,
    zipValidationRequestToken,
  ])

  return (
    <>
      <Toast
        key={toastKey}
        message={toastMessage ?? ''}
        visible={toastVisible}
        onClose={handleToastClose}
      />
      <form
        onSubmit={formik.handleSubmit}
        onFocusCapture={handleFirstInteraction}
        className="flex flex-col gap-10 md:gap-[60px] w-full [&_input:disabled]:bg-gray-100 [&_input:disabled]:cursor-not-allowed"
      >
        {bannerError && (
          <div
            role="alert"
            className="w-full max-w-[1130px] rounded-[6px] border border-red-brand bg-red-50 px-5 py-4 text-sm text-red-brand font-ttHovesRegular"
          >
            {bannerError}
          </div>
        )}
        <section className="flex flex-col gap-5">
          <div className="text-3xl md:text-5xl text-[#2D433F] font-ttHovesBold">
            Freight Cost Estimate
          </div>

          {status === 'unauthenticated' && (
            <div className="px-5 md:px-[30px] py-5 border-[#1F5C48] border-[0.33px] bg-[#F1F9F5] rounded-[6px] w-full max-w-[1130px] flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
              <div className="flex flex-col gap-2">
                <div className="font-ttHovesDemiBold text-[#1F5C48] text-xl md:text-[24px]">
                  New to North Park?
                </div>

                <div className="font-ttHovesRegular text-sm md:text-[16px]">
                  Setting up an account connects you with our team to establish
                  custom rates built around your lanes and volume.
                </div>

                <div className="font-ttHovesRegular text-sm md:text-[16px]">
                  Already have an account? Please{' '}
                  <button
                    type="button"
                    onClick={() => router.push('/login')}
                    className="underline"
                  >
                    sign in.
                  </button>
                </div>
              </div>

              <a
                href="mailto:sales@nopk.biz?subject=Freight%20Cost%20Estimate%20Inquiry&body=Hello"
                className="px-4 py-2 rounded bg-[#53AC7F] text-white font-ttHovesMedium leading-none w-full md:w-auto md:shrink-0 text-center"
              >
                Contact Sales To Get Going
              </a>
            </div>
          )}
        </section>

        {showCustomerDropdown && (
          <section className="flex flex-col gap-5">
            <div className="text-[#2D433F] font-ttHovesMedium text-xl md:text-2xl">
              Customer
            </div>
            <SelectDropdown
              id="quoteCustomer"
              name="quoteCustomer"
              labelText="Select Your Customer"
              placeholder="Select a customer"
              options={customerOptions}
              value={selected3PLManagedAccount}
              handleChange={handleCustomerChange}
              handleBlur={handleCustomerBlur}
              disabled={formik.isSubmitting}
              width="w-full sm:w-[320px]"
              required
              showDefaultOption
            />
          </section>
        )}

        <section className="flex flex-col gap-8 md:gap-[40px]">
          <div className="flex gap-4">
            <div className="text-[#2D433F] font-ttHovesMedium text-xl md:text-2xl">
              Pickup &amp; Delivery Information
            </div>
            {isValidatingZips && <Spinner />}
          </div>
          <div className="flex flex-wrap gap-4 items-start md:gap-6">
            <div className="flex relative flex-col mb-2 w-full sm:w-[320px]">
              <label
                htmlFor="originAddressSearch"
                className="text-green-100 text-sm mb-[7px] font-ttHovesMedium"
              >
                Origin Address <span className="">*</span>
              </label>
              <input
                ref={originAddressRef}
                aria-invalid={originAddressHasError ? 'true' : 'false'}
                className={`rounded-[4px] border ${
                  originAddressHasError
                    ? 'border-red-brand focus:ring-red-brand'
                    : 'border-[#BDBDBD] focus:ring-nptDGreen'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 text-sm disabled:bg-gray-100`}
                disabled={formik.isSubmitting}
                id="originAddressSearch"
                name="originAddressSearch"
                onBlur={() =>
                  formik.setFieldTouched('originAddressLine1', true)
                }
                onChange={(e) =>
                  handleAddressSearchChange('origin', e.target.value)
                }
                onKeyDown={(e) => handleAddressKeyDown('origin', e)}
                placeholder="Start typing an address"
                type="text"
                autoComplete="new-password"
              />
              {originPredictions.length > 0 && (
                <div className="absolute top-full left-0 z-20 mt-1 w-full rounded border border-[#BDBDBD] bg-white shadow-md">
                  {originPredictions.map((prediction, i) => (
                    <button
                      key={prediction.placeId ?? prediction.text.toString()}
                      type="button"
                      className={`block w-full px-3 py-2 text-left text-sm text-[#2D433F] hover:bg-[#F1F9F5] ${
                        i === originActiveIndex ? 'bg-[#F1F9F5]' : ''
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleAddressPredictionSelect('origin', prediction)
                      }}
                    >
                      {prediction.text.toString()}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-2">
                <InputCheckbox
                  name="originAddressNotFound"
                  labelText="Address not found"
                  checked={originManualAddress}
                  fontSize="text-xs"
                  onChange={() =>
                    handleManualAddressToggle('origin', !originManualAddress)
                  }
                />
              </div>
            </div>
            <InputGroup
              label="Origin Address Line 2"
              type="text"
              placeholder="Suite, unit, dock, etc. (optional)"
              name="originAddressLine2"
              value={formik.values.originAddressLine2}
              width="w-full sm:w-[320px]"
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              disabled={formik.isSubmitting}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-start md:gap-6">
            <InputGroup
              label="Origin Zip"
              max={5}
              type="text"
              placeholder=""
              name="originZip"
              value={formik.values.originZip}
              width="w-full sm:w-[150px]"
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              required
              error={formik.errors.originZip}
              touched={formik.submitCount > 0 || formik.touched.originZip}
              disabled={!originManualAddress}
            />
            <InputGroup
              label="City"
              name="originCity"
              font="font-ttHovesMedium"
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              placeholder=""
              value={formik.values.originCity}
              width="w-[calc(70%-0.5rem)] sm:w-[150px]"
              disabled={!originManualAddress || !zipsValidated}
            />
            <InputGroup
              label="State"
              name="originState"
              font="font-ttHovesMedium"
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              placeholder=""
              value={formik.values.originState}
              width="w-[calc(30%-0.5rem)] sm:w-[80px]"
              disabled={!originManualAddress || !zipsValidated}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-start md:gap-6">
            <div className="flex relative flex-col mb-2 w-full sm:w-[320px]">
              <label
                htmlFor="destinationAddressSearch"
                className="text-green-100 text-sm mb-[7px] font-ttHovesMedium"
              >
                Destination Address <span className="">*</span>
              </label>
              <input
                ref={destinationAddressRef}
                aria-invalid={destinationAddressHasError ? 'true' : 'false'}
                className={`rounded-[4px] border ${
                  destinationAddressHasError
                    ? 'border-red-brand focus:ring-red-brand'
                    : 'border-[#BDBDBD] focus:ring-nptDGreen'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 text-sm disabled:bg-gray-100`}
                disabled={formik.isSubmitting}
                id="destinationAddressSearch"
                name="destinationAddressSearch"
                onBlur={() =>
                  formik.setFieldTouched('destinationAddressLine1', true)
                }
                onChange={(e) =>
                  handleAddressSearchChange('destination', e.target.value)
                }
                onKeyDown={(e) => handleAddressKeyDown('destination', e)}
                placeholder="Start typing an address"
                type="text"
                autoComplete="new-password"
              />
              {destinationPredictions.length > 0 && (
                <div className="absolute top-full left-0 z-20 mt-1 w-full rounded border border-[#BDBDBD] bg-white shadow-md">
                  {destinationPredictions.map((prediction, i) => (
                    <button
                      key={prediction.placeId ?? prediction.text.toString()}
                      type="button"
                      className={`block w-full px-3 py-2 text-left text-sm text-[#2D433F] hover:bg-[#F1F9F5] ${
                        i === destinationActiveIndex ? 'bg-[#F1F9F5]' : ''
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleAddressPredictionSelect('destination', prediction)
                      }}
                    >
                      {prediction.text.toString()}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-2">
                <InputCheckbox
                  name="destinationAddressNotFound"
                  labelText="Address not found"
                  checked={destinationManualAddress}
                  fontSize="text-xs"
                  onChange={() =>
                    handleManualAddressToggle(
                      'destination',
                      !destinationManualAddress
                    )
                  }
                />
              </div>
            </div>
            <InputGroup
              label="Destination Address Line 2"
              type="text"
              placeholder="Suite, unit, dock, etc. (optional)"
              name="destinationAddressLine2"
              value={formik.values.destinationAddressLine2}
              width="w-full sm:w-[320px]"
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              disabled={formik.isSubmitting}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end md:gap-6">
            <InputGroup
              label="Destination Zip"
              max={5}
              type="text"
              placeholder=""
              name="destinationZip"
              value={formik.values.destinationZip}
              width="w-full sm:w-[150px]"
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              required
              error={formik.errors.destinationZip}
              touched={formik.submitCount > 0 || formik.touched.destinationZip}
              disabled={!destinationManualAddress}
            />
            <InputGroup
              name="destinationCity"
              font="font-ttHovesMedium"
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              label="City"
              placeholder=""
              value={formik.values.destinationCity}
              width="w-[calc(70%-0.5rem)] sm:w-[150px]"
              disabled={!destinationManualAddress || !zipsValidated}
            />
            <InputGroup
              label="State"
              name="destinationState"
              font="font-ttHovesMedium"
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              placeholder=""
              value={formik.values.destinationState}
              width="w-[calc(30%-0.5rem)] sm:w-[80px]"
              disabled={!destinationManualAddress || !zipsValidated}
            />
          </div>
          {destinationInterlineNotice && (
            <div className="text-sm text-gray-60 font-ttHovesRegular">
              NPT uses an Interline Partner for delivery to this zip code.
            </div>
          )}
          {zipValidationError && (
            <div className="text-sm text-red-brand font-ttHovesRegular">
              {zipValidationError}
            </div>
          )}
          <hr className="border border-gray-[#4F4F4F]" />
        </section>

        <section className="flex flex-col gap-8 md:gap-[40px]">
          <div className="text-[#2D433F] font-ttHovesMedium text-xl md:text-2xl">
            Shipment Details
          </div>

          <div className="flex flex-col gap-4">
            {formik.values.shipmentItems.map((item, index) => {
              const itemErrors = formik.errors.shipmentItems as
                | Array<{
                    weight?: string
                    freightClass?: string
                    length?: string
                    width?: string
                    height?: string
                    handlingUnits?: string
                  }>
                | undefined
              const itemTouched = formik.touched.shipmentItems as
                | Array<{
                    weight?: boolean
                    freightClass?: boolean
                    length?: boolean
                    width?: boolean
                    height?: boolean
                    handlingUnits?: boolean
                  }>
                | undefined
              const showError = formik.submitCount > 0
              return (
                <div
                  key={index}
                  className="flex flex-col gap-4 px-4 py-4 rounded-md border-[0.33px] border-gray-400 md:flex-row md:gap-5 md:justify-between md:items-center md:px-6"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex flex-wrap gap-4 items-end md:gap-6">
                      <InputGroup
                        label="Total Weight (lbs)"
                        type="number"
                        placeholder=""
                        name={`shipmentItems[${index}].weight`}
                        value={item.weight}
                        width="w-full sm:w-[150px]"
                        handleChange={handleNonNegativeNumberChange}
                        handleBlur={formik.handleBlur}
                        required
                        error={itemErrors?.[index]?.weight}
                        touched={showError || itemTouched?.[index]?.weight}
                        disabled={!zipsValidated || formik.isSubmitting}
                      />
                      <SelectDropdown
                        name={`shipmentItems[${index}].freightClass`}
                        font="font-ttHovesMedium"
                        marginBottom="mb-2"
                        handleBlur={formik.handleBlur}
                        handleChange={formik.handleChange}
                        labelText="Freight Class"
                        tooltip={<FreightClassTooltip />}
                        placeholder=""
                        options={FREIGHT_CLASSES}
                        value={item.freightClass}
                        width="w-full sm:w-[150px]"
                        showDefaultOption={true}
                        required
                        error={itemErrors?.[index]?.freightClass}
                        touched={
                          showError || itemTouched?.[index]?.freightClass
                        }
                        disabled={!zipsValidated || formik.isSubmitting}
                      />
                    </div>
                    <div className="flex flex-wrap gap-4 items-end md:gap-6">
                      <InputGroup
                        label="Length (In.)"
                        type="number"
                        placeholder=""
                        name={`shipmentItems[${index}].length`}
                        value={item.length ?? ''}
                        width="w-[calc(50%-0.5rem)] sm:w-[150px]"
                        handleChange={handleNonNegativeNumberChange}
                        handleBlur={formik.handleBlur}
                        required
                        error={itemErrors?.[index]?.length}
                        touched={showError || itemTouched?.[index]?.length}
                        disabled={!zipsValidated || formik.isSubmitting}
                      />
                      <InputGroup
                        label="Width (In.)"
                        type="number"
                        placeholder=""
                        name={`shipmentItems[${index}].width`}
                        value={item.width ?? ''}
                        width="w-[calc(50%-0.5rem)] sm:w-[150px]"
                        handleChange={handleNonNegativeNumberChange}
                        handleBlur={formik.handleBlur}
                        required
                        error={itemErrors?.[index]?.width}
                        touched={showError || itemTouched?.[index]?.width}
                        disabled={!zipsValidated || formik.isSubmitting}
                      />
                      <InputGroup
                        label="Height (In.)"
                        type="number"
                        placeholder=""
                        name={`shipmentItems[${index}].height`}
                        value={item.height ?? ''}
                        width="w-[calc(50%-0.5rem)] sm:w-[150px]"
                        handleChange={handleNonNegativeNumberChange}
                        handleBlur={formik.handleBlur}
                        required
                        error={itemErrors?.[index]?.height}
                        touched={showError || itemTouched?.[index]?.height}
                        disabled={!zipsValidated || formik.isSubmitting}
                      />
                      <InputGroup
                        label="Handling Units"
                        type="number"
                        placeholder=""
                        name={`shipmentItems[${index}].handlingUnits`}
                        value={item.handlingUnits ?? ''}
                        width="w-[calc(50%-0.5rem)] sm:w-[150px]"
                        handleChange={handleNonNegativeNumberChange}
                        handleBlur={formik.handleBlur}
                        required
                        error={itemErrors?.[index]?.handlingUnits}
                        touched={
                          showError || itemTouched?.[index]?.handlingUnits
                        }
                        disabled={!zipsValidated || formik.isSubmitting}
                      />
                    </div>
                    <div className="flex items-center my-2">
                      <InputCheckbox
                        name={`shipmentItems[${index}].hazardous`}
                        labelText="Contains Hazardous Materials?"
                        checked={item.hazardous}
                        onChange={() => toggleItemHazardous(index)}
                      />
                    </div>
                    <InputGroup
                      label="Brief description of freight"
                      placeholder=""
                      name={`shipmentItems[${index}].description`}
                      width=""
                      value={item.description}
                      handleChange={formik.handleChange}
                      handleBlur={formik.handleBlur}
                      disabled={!zipsValidated || formik.isSubmitting}
                    />
                  </div>
                  <div
                    className={`flex justify-end md:block md:shrink-0 ${
                      index === 0 ? 'hidden md:block' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={
                        index > 0 ? () => removeShipmentItem(index) : undefined
                      }
                      tabIndex={index > 0 ? undefined : -1}
                      aria-hidden={index === 0 ? 'true' : undefined}
                      aria-label="Remove item"
                      className={`p-1 md:mb-[10px] font-ttHovesMedium text-sm transition-colors ${
                        index === 0
                          ? 'invisible pointer-events-none'
                          : 'hover:text-red-500 text-[#2D433F]'
                      }`}
                    >
                      Remove Item
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <button
            type="button"
            onClick={addShipmentItem}
            className="bg-[#F1F9F5] px-4 py-2 rounded gap-2 flex w-fit"
            disabled={!zipsValidated || formik.isSubmitting}
          >
            <ExitIcon
              additionalClasses="rotate-45"
              width="w-[16px]"
              height="h-[16px]"
            />
            <span className="font-ttHovesMedium text-sm text-[#2D433F]">
              Add Item
            </span>
          </button>

          <div className="flex flex-wrap gap-8">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-ttHovesMedium text-[#2D433F]">
                Total Cubic Feet
              </div>
              <div className="text-sm font-ttHovesMedium text-[#2D433F]">
                {computedTotalCubicFeet > 0
                  ? computedTotalCubicFeet.toFixed(2)
                  : '—'}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-ttHovesMedium text-[#2D433F]">
                Total Pounds
              </div>
              <div className="text-sm font-ttHovesMedium text-[#2D433F]">
                {computedTotalWeight > 0
                  ? computedTotalWeight.toLocaleString()
                  : '—'}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-ttHovesMedium text-[#2D433F]">
                PCF (lbs/cu ft)
              </div>
              <div className="text-sm font-ttHovesMedium text-[#2D433F]">
                {computedPoundsPerCubicFoot > 0
                  ? computedPoundsPerCubicFoot.toFixed(2)
                  : '—'}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-ttHovesMedium text-[#2D433F]">
                Total Handling Units
              </div>
              <div className="text-sm font-ttHovesMedium text-[#2D433F]">
                {computedTotalHandlingUnits > 0
                  ? computedTotalHandlingUnits.toLocaleString()
                  : '—'}
              </div>
            </div>
          </div>
          {volumePricingError && (
            <div className="text-sm text-red-brand font-ttHovesRegular">
              {volumePricingError}
            </div>
          )}
          <hr className="border border-gray-[#4F4F4F]" />
        </section>

        <section className="flex flex-col gap-8 md:gap-[40px]">
          <div className="text-[#2D433F] font-ttHovesMedium text-xl md:text-2xl">
            Additional Services
          </div>
          <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {ACCESSORIALS.map(({ code, label }) => {
              const checked = formik.values.accessorials.includes(code)
              return (
                <InputCheckbox
                  key={code}
                  name={`accessorials.${code}`}
                  labelText={label}
                  checked={checked}
                  onChange={() =>
                    formik.setFieldValue(
                      'accessorials',
                      checked
                        ? formik.values.accessorials.filter((c) => c !== code)
                        : [...formik.values.accessorials, code]
                    )
                  }
                />
              )
            })}
          </div>
          {liftgateLimitError && (
            <div className="text-sm text-red-brand font-ttHovesRegular">
              {liftgateLimitError}
            </div>
          )}
        </section>

        <section className="pt-8 pb-16 md:pt-[40px] md:pb-[120px] flex flex-col gap-4 sm:items-end">
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="px-6 py-3 bg-[#2D433F] rounded w-full sm:w-fit disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-white font-ttHovesMedium">
              {formik.isSubmitting ? 'Loading...' : 'Get Your Estimate'}
            </span>
          </button>
        </section>
      </form>
    </>
  )
}

export default RateEstimateForm
